/**
 * The site's animation vocabulary, as pure data and pure functions.
 *
 * Deliberately free of any import: the timing of these sequences *is* the
 * design, so it is expressed somewhere a unit test can reach without a
 * browser, a DOM, or GSAP. js/motion.js consumes this and does the wiring.
 * This mirrors the split already used for the enquiry form, where
 * js/validation.js holds the rules and js/enquiry.js touches the page.
 *
 * The vocabulary is borrowed from the delivery systems the company builds, so
 * a movement on the page means the same thing it means in a pipeline. There
 * are four sequences and each one is a deliberate reference:
 *
 *   rollout    A progressive delivery. The first element lands alone, the way
 *              a canary takes 1% of traffic, and once it is up the remainder
 *              follow in doubling waves. Used for general content groups.
 *
 *   pipeline   CI/CD stages. Strictly sequential and evenly spaced: a stage
 *              starts only once the one before it has finished, and its marker
 *              goes from pending to passed as it does. Used for the numbered
 *              step and journey lists.
 *
 *   provision  Replicas coming up under an autoscaler. Near-simultaneous with
 *              a small jitter, scaling up from slightly under full size and
 *              overshooting a touch as they settle. Used for card grids.
 *
 *   logStream  Lines arriving in a log tail. Uniform, quick, unbounded — no
 *              cap on the stagger, because the tenth line should still feel
 *              like it is being written. Used for FAQ lists and log panes.
 *
 * The point of naming them is that a new section picks a sequence by asking
 * what kind of thing it is, rather than by inventing another duration.
 */

/**
 * Timing for each sequence. Durations and gaps are in seconds, GSAP's unit.
 *
 * @typedef {{ duration: number, gap: number, ease: string, from: Record<string, number>, waves: boolean, maxGap: number }} Sequence
 * @type {Record<string, Sequence>}
 */
export const SEQUENCES = {
  // Canary → 10% → 100%. The doubling wave is the whole point of the shape.
  rollout: {
    duration: 0.62,
    gap: 0.13,
    maxGap: 0.52,
    ease: 'power3.out',
    from: { opacity: 0, y: 22, rotateX: 6 },
    waves: true,
  },

  // A stage cannot start before the previous one passes, so the gap is fixed
  // and never compressed — the sequence reads as a queue, not as a burst.
  pipeline: {
    duration: 0.5,
    gap: 0.16,
    maxGap: 1.6,
    ease: 'power2.out',
    from: { opacity: 0, y: 18 },
    waves: false,
  },

  // Replicas do not come up in an order that means anything, so this is close
  // to simultaneous. The overshoot is the scheduler landing a pod.
  provision: {
    duration: 0.55,
    gap: 0.05,
    maxGap: 0.3,
    ease: 'back.out(1.5)',
    from: { opacity: 0, scale: 0.94, y: 10 },
    waves: false,
  },

  // A tail has no last line, so the gap is deliberately uncapped.
  logStream: {
    duration: 0.28,
    gap: 0.09,
    maxGap: Infinity,
    ease: 'none',
    from: { opacity: 0, x: -10 },
    waves: false,
  },
};

/** The sequence used when a group does not name one. */
export const DEFAULT_SEQUENCE = 'rollout';

/**
 * Map a `data-motion` attribute value onto a sequence name.
 * Accepts the kebab-case spelling the markup uses.
 *
 * @param {string | null | undefined} value
 * @returns {keyof typeof SEQUENCES}
 */
export function resolveSequence(value) {
  if (!value) return DEFAULT_SEQUENCE;
  const camel = String(value)
    .trim()
    .replace(/-([a-z])/g, (_, char) => char.toUpperCase());
  return camel in SEQUENCES ? /** @type {keyof typeof SEQUENCES} */ (camel) : DEFAULT_SEQUENCE;
}

/**
 * The start time, in seconds, of each element in a group.
 *
 * Pure so the shape of every sequence is unit-testable without a browser —
 * these offsets are the actual design, and a regression here is a regression
 * in how the site feels.
 *
 * @param {number} count how many elements are in the group
 * @param {keyof typeof SEQUENCES} name
 * @returns {number[]} one offset per element, ascending, starting at 0
 */
export function stageDelays(count, name = DEFAULT_SEQUENCE) {
  const sequence = SEQUENCES[name] ?? SEQUENCES[DEFAULT_SEQUENCE];
  if (count <= 0) return [];

  const delays = [];

  if (sequence.waves) {
    // Doubling waves: 1 element, then 2, then 4, then 8. Every element in a
    // wave starts together, and each wave is one gap behind the last.
    let index = 0;
    let wave = 0;
    let waveSize = 1;

    while (index < count) {
      const offset = Math.min(wave * sequence.gap, sequence.maxGap);
      for (let i = 0; i < waveSize && index < count; i += 1, index += 1) {
        delays.push(offset);
      }
      wave += 1;
      waveSize *= 2;
    }

    return delays;
  }

  for (let i = 0; i < count; i += 1) {
    delays.push(Math.min(i * sequence.gap, sequence.maxGap));
  }

  return delays;
}

/**
 * Total time a group takes to land, including the last element's own duration.
 * Used to size the watchdog and to assert the sequences stay quick.
 *
 * @param {number} count
 * @param {keyof typeof SEQUENCES} name
 * @returns {number} seconds
 */
export function sequenceDuration(count, name = DEFAULT_SEQUENCE) {
  const sequence = SEQUENCES[name] ?? SEQUENCES[DEFAULT_SEQUENCE];
  const delays = stageDelays(count, name);
  if (!delays.length) return 0;
  return delays[delays.length - 1] + sequence.duration;
}
